import moment from "moment";
import Icon from "@shared/components/ui-kit/icons/Icon.web";
import { useTranslation } from "next-i18next";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useIsSmallerThanSm } from "@shared/hooks/useIsSmallerThan";
import { useRouter } from "next/router";
import { getStaticEventDateRange } from "@shared/modules/events/utils/event-utils";
import { Badge, ChildBadgeProps } from "@ui-kit/data-display/Badge/Badge";

const ENGLISH_DATE_FORMAT = "dddd MMMM Do, YYYY";
const FRENCH_DATE_FORMAT = "dddd D MMMM, YYYY";

export interface CalendarBadgeProps extends ChildBadgeProps {
  startDate: string;
  endDate: string;
  timezone?: string;
  isTBD?: boolean;
  showLabels?: boolean;
  showEndTime?: boolean;
}

export const CalendarBadgeText = ({
  startDate,
  endDate,
  isTBD,
  timezone = moment.tz.guess(),
  showLabels = true,
  showEndTime = true,
}: CalendarBadgeProps) => {
  const isSmallerThanSm = useIsSmallerThanSm();
  const router = useRouter();
  const { t } = useTranslation("purchases");

  if (isTBD) {
    return <Text>{t("TBD")}</Text>;
  }

  const startLabel = showLabels && showEndTime ? `${t("Starts")}: ` : "";
  const endLabel = showLabels ? `${t("Ends")}: ` : "";
  const fullDateFormat =
    router.locale === "en" ? ENGLISH_DATE_FORMAT : FRENCH_DATE_FORMAT;

  const formattedStartDate = moment(startDate)
    .tz(timezone)
    .format(fullDateFormat);
  const formattedEndDate = moment(endDate).tz(timezone).format(fullDateFormat);

  if (isSmallerThanSm) {
    return (
      <Text>
        {getStaticEventDateRange(
          { starts_on: startDate, ends_on: endDate, timezone },
          "TBD"
        )}
      </Text>
    );
  }

  // labels if appropriate, full date formats on multiple lines
  return (
    <Flex direction="column">
      <Text>{`${startLabel}${formattedStartDate}`}</Text>
      <Text hidden={!showEndTime}>{`${endLabel}${formattedEndDate}`}</Text>
    </Flex>
  );
};

export const CalendarBadge = ({
  isVisible = true,
  ...props
}: CalendarBadgeProps) => {
  const icon = (
    <Icon.Calendar mr="xs" colorPalette="primary" color="gray.600" size="sm" />
  );
  const text = <CalendarBadgeText {...props} />;

  return (
    <Badge
      icon={icon}
      text={text}
      isVisible={isVisible}
      data-testid="calendar-badge"
    />
  );
};
